import type { ThemeRegistration } from 'shiki'

import { darculaTheme } from '../theme/darcula'
import { solarizedTheme } from '../theme/solarize'
import type { Theme } from '../theme/types/theme'
import { ThemeType } from '../theme/types/themeType'

export type FPlusThemeProps = {
  additionalColors?: (
    themeType: ThemeType,
    theme: Theme,
  ) => Record<string, string>
}

/**
 * What the diff renderer paints an added, removed and changed line with. It
 * reads these three keys off the theme and derives the rest of a diff — the row
 * tints, the gutter, the word-level emphasis — from them, so naming them here
 * is enough to put a diff in the portal's colours rather than the renderer's
 * own brand ones.
 *
 * They are the portal's semantic tokens, written out because a theme is handed
 * to Monaco as well, which resolves no custom properties: `--color-success`,
 * `--color-error` and `--color-info`. Each theme takes the shade that holds
 * contrast against its own surface rather than the 500s the portal uses on
 * paper: the light theme the darker 700s, the dark theme the lighter 400s.
 */
const lightDiffColors: Record<string, string> = {
  'gitDecoration.addedResourceForeground': '#007a55',
  'gitDecoration.deletedResourceForeground': '#c10007',
  'gitDecoration.modifiedResourceForeground': '#1447e6',
}

const darkDiffColors: Record<string, string> = {
  'gitDecoration.addedResourceForeground': '#00d492',
  'gitDecoration.deletedResourceForeground': '#ff6467',
  'gitDecoration.modifiedResourceForeground': '#50a2ff',
}

// reference https://ethanschoonover.com/solarized/
const lightColors: Record<string, string> = {
  'editor.foreground': '#657b83',
  'editor.background': '#f2f0eb',
  'editor.selectionBackground': '#eee8d5',
  'editor.lineHighlightBackground': '#eee8d5',
  'editorCursor.foreground': '#073642',
  'editorWhitespace.foreground': '#EAE3C9',
  ...lightDiffColors,
}

// reference: https://github.com/rokoroku/vscode-theme-darcula/blob/master/themes/darcula.json
const darkColors: Record<string, string> = {
  'editor.background': '#242424',
  'editor.foreground': '#CC8242',
  'editor.selectionBackground': '#204182cc',
  'editor.lineHighlightBackground': '#ffffff0b',
  'editor.activeLineNumber.foreground': '#cccccc',
  'editorGutter.background': '#ffffff0b',
  'titleBar.activeBackground': '#ffffff1a',
  'titleBar.inactiveBackground': '#ffffff10',
  'titleBar.activeForeground': '#cccccc',
  'sideBar.border': '#ffffff1a',
  'sideBar.background': '#242424',
  'statusBar.foreground': '#cccccc',
  'statusBar.background': '#ffffff1a',
  'tab.activeBackground': '#ffffff1a',
  'tab.inactiveBackground': '#ffffff0b',
  'tab.activeBorder': '#0c7d9d',
  'editorSuggestWidget.background': '#303030',
  'editorSuggestWidget.highlightForeground': '#cc6e2f',
  'editorSuggestWidget.selectedBackground': '#204182cc',
  'badge.background': '#ffffff1a',
  'activityBar.background': '#ffffff1a',
  'activityBarBadge.background': '#007acc',
  ...darkDiffColors,
}

const makeSettings = (theme: Theme): ThemeRegistration['settings'] => [
  { scope: ['comment'], settings: { foreground: theme.comment } },
  { scope: ['invalid'], settings: { foreground: theme.error } },
  { scope: ['constant.language'], settings: { foreground: theme.literal } },
  {
    scope: ['string'],
    settings: { foreground: theme.string, fontStyle: 'italic' },
  },
  // An escaped character keeps the keyword colour it had before the migration.
  {
    scope: ['constant.character.escape'],
    settings: { foreground: theme.keyword },
  },
  { scope: ['constant.numeric'], settings: { foreground: theme.number } },
  { scope: ['keyword'], settings: { foreground: theme.keyword } },
  {
    scope: ['keyword.operator', 'punctuation'],
    settings: { foreground: theme.operator },
  },
  { scope: ['variable'], settings: { foreground: theme.variable } },
]

const createTextMateTheme = (
  themeType: ThemeType,
  theme: Theme,
  colors: Record<string, string>,
  { additionalColors = () => ({}) }: FPlusThemeProps,
): ThemeRegistration => ({
  name: themeType,
  type: themeType === ThemeType.light ? 'light' : 'dark',
  colors: { ...colors, ...additionalColors(themeType, theme) },
  settings: makeSettings(theme),
})

/**
 * The F-plus TextMate themes. They are the single source of truth for F-plus
 * colours: Shiki converts them for Monaco and the diff renderer consumes them
 * directly.
 */
export function createFPlusTextMateThemes(
  props: FPlusThemeProps = {},
): ThemeRegistration[] {
  return [
    createTextMateTheme(ThemeType.light, solarizedTheme, lightColors, props),
    createTextMateTheme(ThemeType.dark, darculaTheme, darkColors, props),
  ]
}
